import { ApiService } from '@supply-mover-2-0/core';
import type { MyInAppAssistantDto } from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
	type ReactNode,
} from 'react';
import { AiAgentOverlayManager } from '../../ai-agent/services/AiAgentOverlayManager';
import { useAgentChatData } from '../../providers/AgentChatDataProvider';

export interface WidgetAgent {
	id: number;
	name: string;
	faIcon?: string | null;
	agentColor?: string | null;
	agentType: string;
	enableVoice?: boolean;
}

export interface WidgetSession {
	id: number;
	createdDate: string;
	endDate?: string | null;
	status: string;
	sessionType: string;
	agentName?: string;
	agentFaIcon?: string | null;
	agentColor?: string | null;
}

type WidgetView = 'home' | 'active-chat' | 'history-detail';

interface AgentWidgetState {
	isOpen: boolean;
	view: WidgetView;
	defaultAgent: WidgetAgent | null;
	sessions: Array<WidgetSession>;
	sessionsLoading: boolean;
	activeSessionId: string | null;
	selectedHistorySession: number | null;
	selectedMode: 'chat' | 'voice' | null;
	loading: boolean;
	error: string | null;
	/**
	 * PD-16920 — the user's own assistant pick and the agents they may pick
	 * from; loaded when the widget opens.
	 */
	myAssistant: MyInAppAssistantDto | null;
	choosingAssistant: boolean;
}

interface AgentWidgetContextValue extends AgentWidgetState {
	toggleWidget: () => void;
	openWidget: () => void;
	closeWidget: () => void;
	setView: (view: WidgetView) => void;
	viewHistorySession: (sessionId: number) => void;
	selectMode: (mode: 'chat' | 'voice') => void;
	goHome: () => void;
	refreshSessions: () => Promise<void>;
	/**
	 * PD-16920 — pick the user's own assistant (an agent id), or null to go
	 * back to their role's or the tenant's.
	 */
	chooseAssistant: (agentId: number | null) => Promise<void>;
}

const AgentChatWidgetContext = createContext<AgentWidgetContextValue | null>(
	null,
);

export const useAgentWidget = (): AgentWidgetContextValue => {
	const context = useContext(AgentChatWidgetContext);

	if (context == null) {
		throw new Error(
			'useAgentWidget must be used within an AgentChatWidgetProvider',
		);
	}

	return context;
};

interface AgentChatWidgetProviderProps {
	children: ReactNode;
}

export const AgentChatWidgetProvider: React.FC<
	AgentChatWidgetProviderProps
> = ({ children }) => {
	// Preloaded data from the data provider — when `enabled` is false, no
	// in-app assistant resolved for this user and the widget hides itself.
	const preloadedData = useAgentChatData();

	const [state, setState] = useState<AgentWidgetState>(() => ({
		isOpen: false,
		view: 'home',
		defaultAgent: null,
		sessions: [],
		sessionsLoading: false,
		activeSessionId: null,
		selectedHistorySession: null,
		selectedMode: null,
		loading: preloadedData?.systemAgentData == null,
		error: null,
		myAssistant: null,
		choosingAssistant: false,
	}));

	// Sync the resolved assistant into widget state once preload completes.
	useEffect(() => {
		if (preloadedData?.systemAgentData == null) {
			return;
		}

		const data = preloadedData.systemAgentData;
		const agent = data.agent;

		if (data.enabled && agent != null) {
			setState((prev) => ({
				...prev,
				defaultAgent: {
					id: agent.id,
					name: agent.name,
					faIcon: agent.faIcon,
					agentColor: agent.agentColor,
					agentType: 'assistant',
					enableVoice: agent.enableVoice,
				},
				loading: false,
			}));
		} else {
			setState((prev) => ({
				...prev,
				defaultAgent: null,
				loading: false,
			}));
		}
	}, [preloadedData?.systemAgentData]);

	const fetchSessions = useCallback(async () => {
		const defaultAgent = state.defaultAgent;

		if (defaultAgent == null) {
			return;
		}

		// PD-16920: the assistant is an ordinary agent, so its history is
		// filtered by agent id.
		setState((prev) => ({ ...prev, sessionsLoading: true }));

		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: Array<WidgetSession>;
			}>(
				`/ai-agents/chat-widget/my-sessions?limit=10&agentId=${defaultAgent.id}`,
			);

			if (response.success && Array.isArray(response.data)) {
				setState((prev) => ({
					...prev,
					sessions: response.data ?? [],
					sessionsLoading: false,
				}));
			} else {
				setState((prev) => ({
					...prev,
					sessions: [],
					sessionsLoading: false,
				}));
			}
		} catch (error) {
			console.debug('Failed to fetch sessions:', error);
			setState((prev) => ({
				...prev,
				sessions: [],
				sessionsLoading: false,
			}));
		}
	}, [state.defaultAgent]);

	// Reload the history when the widget opens, and when the assistant changes
	// (the history is the current assistant's).
	useEffect(() => {
		if (state.isOpen && !state.sessionsLoading && state.defaultAgent != null) {
			void fetchSessions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.isOpen, state.defaultAgent?.id]);

	const fetchMyAssistant = useCallback(async () => {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: MyInAppAssistantDto;
			}>('/ai-agents/chat-widget/my-assistant');
			if (response.success && response.data != null) {
				const myAssistant = response.data;
				setState((prev) => ({ ...prev, myAssistant }));
			}
		} catch (error) {
			// The picker simply does not show; the widget still works.
			console.debug('Failed to fetch the assistant choices:', error);
		}
	}, []);

	useEffect(() => {
		if (state.isOpen && state.myAssistant == null) {
			void fetchMyAssistant();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.isOpen]);

	const refreshPreloaded = preloadedData?.refresh;
	const chooseAssistant = useCallback(
		async (agentId: number | null) => {
			setState((prev) => ({ ...prev, choosingAssistant: true }));
			try {
				const response =
					agentId != null
						? await ApiService.apiPut<{
								success: boolean;
								data?: MyInAppAssistantDto;
							}>('/ai-agents/chat-widget/my-assistant', { agentId })
						: await ApiService.apiDelete<{
								success: boolean;
								data?: MyInAppAssistantDto;
							}>('/ai-agents/chat-widget/my-assistant');
				const myAssistant = response.data ?? null;
				setState((prev) => ({ ...prev, myAssistant }));
				// The widget's agent comes from the preloaded resolution; fetch it
				// again so the new assistant takes over.
				await refreshPreloaded?.();
			} catch (error) {
				console.debug('Failed to change the assistant:', error);
			} finally {
				setState((prev) => ({ ...prev, choosingAssistant: false }));
			}
		},
		[refreshPreloaded],
	);

	const toggleWidget = useCallback(() => {
		setState((prev) => ({
			...prev,
			isOpen: !prev.isOpen,
			view: prev.isOpen ? 'home' : prev.view,
		}));
	}, []);

	const openWidget = useCallback(() => {
		setState((prev) => ({ ...prev, isOpen: true }));
	}, []);

	const closeWidget = useCallback(() => {
		setState((prev) => ({
			...prev,
			isOpen: false,
			view: 'home',
			selectedHistorySession: null,
		}));
	}, []);

	const setView = useCallback((view: WidgetView) => {
		setState((prev) => ({ ...prev, view }));
	}, []);

	const viewHistorySession = useCallback((sessionId: number) => {
		setState((prev) => ({
			...prev,
			selectedHistorySession: sessionId,
			view: 'history-detail',
		}));
	}, []);

	const selectMode = useCallback(
		(mode: 'chat' | 'voice') => {
			if (state.defaultAgent == null) {
				return;
			}

			// Launch the overlay manager FIRST (outside of setState)
			// This avoids React warning about updating OverlayProvider while rendering AgentChatWidgetProvider.
			// PD-16920: the assistant opens by its agent id.
			AiAgentOverlayManager.getInstance().showAgent(state.defaultAgent.id, {
				initialMode: mode,
				viewMode: mode === 'voice' ? 'modern' : 'chat',
				disableVoice: mode === 'chat',
				disableChat: mode === 'voice',
				agentName: state.defaultAgent.name,
			});

			// THEN close the widget panel
			setState((prev) => ({
				...prev,
				isOpen: false,
				view: 'home',
				selectedMode: null,
			}));
		},
		[state.defaultAgent],
	);

	const goHome = useCallback(() => {
		setState((prev) => ({
			...prev,
			view: 'home',
			selectedHistorySession: null,
			selectedMode: null,
			activeSessionId: null,
		}));
	}, []);

	const refreshSessions = useCallback(async () => {
		await fetchSessions();
	}, [fetchSessions]);

	const value: AgentWidgetContextValue = {
		...state,
		toggleWidget,
		openWidget,
		closeWidget,
		setView,
		viewHistorySession,
		selectMode,
		goHome,
		refreshSessions,
		chooseAssistant,
	};

	return (
		<AgentChatWidgetContext.Provider value={value}>
			{children}
		</AgentChatWidgetContext.Provider>
	);
};
